import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PostCard } from '@/types/blog';
import { 
  EyeIcon, 
  HeartIcon,
  ShareIcon,
  CalendarIcon,
  UserIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface ArticleCardProps {
  post: PostCard;
  featured?: boolean;
  onClick?: (uuid: string) => void;
  onLike?: (uuid: string) => void;
  onShare?: (uuid: string, platform: 'facebook' | 'linkedin' | 'zalo') => void;
  className?: string;
}

const ArticleCard = ({ 
  post, 
  featured = false,
  onClick, 
  onLike,
  onShare,
  className = '' 
}: ArticleCardProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);

  // Handle article click
  const handleClick = () => {
    if (onClick) {
      onClick(post.uuid);
    }
  };

  // Handle like action
  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
    if (onLike) {
      onLike(post.uuid);
    }
  };

  // Handle share action
  const handleShare = (e: React.MouseEvent, platform: 'facebook' | 'linkedin' | 'zalo') => {
    e.preventDefault();
    e.stopPropagation();
    if (onShare) {
      onShare(post.uuid, platform);
    }
  };

  // Format view count
  const formatViewCount = (views: number) => {
    if (views < 1000) return views.toString();
    if (views < 1000000) return `${(views / 1000).toFixed(1)}K`;
    return `${(views / 1000000).toFixed(1)}M`;
  };

  // Format date
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - new Date(date).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Hôm qua';
    if (diffDays <= 7) return `${diffDays} ngày trước`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} tuần trước`;
    if (diffDays <= 365) return `${Math.ceil(diffDays / 30)} tháng trước`;
    return `${Math.ceil(diffDays / 365)} năm trước`;
  };

  // Truncate description
  const truncateDescription = (text: string, maxLength: number = featured ? 150 : 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Get topic badge color
  const getTopicBadgeColor = (topicSlug: string) => {
    const colors: Record<string, string> = {
      'banking-chuyen-tien': 'bg-m2-primary/15 text-m2-primary border-m2-primary/30',
      'database-fundamentals': 'bg-eh-mint text-eh-forest-ink border-eh-border',
      'programming': 'bg-eh-slate text-eh-charcoal border-eh-border',
      'marketing': 'bg-eh-keylime text-eh-forest-ink border-eh-border',
      'business': 'bg-m2-surface-high text-m2-on-surface border-m2-outline-variant',
      'default': 'bg-m2-primary/15 text-m2-primary border-m2-primary/30'
    };
    
    return colors[topicSlug] || colors['default'];
  };

  const cardSizeClasses = featured 
    ? 'h-auto' // Featured cards can be taller
    : 'h-[500px]'; // Regular cards have fixed height

  const imageHeightClasses = featured 
    ? 'h-64' // Taller image for featured
    : 'h-48'; // Standard height

  return (
    <div className={`card group cursor-pointer overflow-hidden rounded-eh border border-eh-border bg-m2-surface-lowest shadow-sm transition-all duration-300 hover:shadow-eh-soft hover:-translate-y-1 ${cardSizeClasses} ${className} relative`}>
      {/* Featured badge */}
      {featured && post.isFeatured && (
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-eh-pill bg-eh-keylime text-eh-forest-ink border border-eh-border">
            <StarIcon className="h-3 w-3" />
            Nổi bật
          </span>
        </div>
      )}

      <Link 
        to={`/blog/articles/${post.uuid}`}
        onClick={handleClick}
        className="block h-full flex flex-col"
      >
        {/* Article Image */}
        <div className={`relative ${imageHeightClasses} overflow-hidden bg-m2-surface-high flex-shrink-0`}>
          {post.avatarUrl && !imageError ? (
            <img
              src={post.avatarUrl}
              alt={post.title}
              className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          ) : (
            // Fallback placeholder
            <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-m2-primary/10 to-m2-primary/20">
              <div className="text-center">
                <UserIcon className="h-12 w-12 text-m2-primary mx-auto mb-2" />
                <p className="text-m2-primary text-sm font-medium">Hình ảnh bài viết</p>
              </div>
            </div>
          )}
          
          {/* Loading overlay */}
          {imageLoading && (
            <div className="absolute inset-0 bg-m2-surface-highest animate-pulse"></div>
          )}

          {/* Topic badge */}
          <div className="absolute top-3 left-3">
            <Link
              to={`/blog/topics/${post.topic.slug}`}
              onClick={(e) => e.stopPropagation()}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-eh-pill border ${getTopicBadgeColor(post.topic.slug)} hover:shadow-sm transition-shadow`}
            >
              {post.topic.title}
            </Link>
          </div>
        </div>

        {/* Article Content */}
        <div className="p-6 flex-grow flex flex-col">
          {/* Article Title */}
          <h3 className={`${featured ? 'text-xl' : 'text-lg'} font-semibold text-m2-on-surface mb-2 group-hover:text-m2-primary transition-colors line-clamp-2`}>
            {post.title}
          </h3>

          {/* Article Description */}
          <p className="text-m2-on-surface-variant text-sm mb-4 leading-relaxed flex-grow">
            {truncateDescription(post.shortDescription)}
          </p>

          {/* Author and Date */}
          <div className="flex items-center gap-4 mb-4 text-sm text-m2-on-surface-variant">
            <div className="flex items-center gap-1">
              <UserIcon className="h-4 w-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-4 w-4" />
              <span>{formatDate(post.createdAt)}</span>
            </div>
          </div>

          {/* Article Meta and Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-m2-outline-variant mt-auto">
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-m2-on-surface-variant">
              <div className="flex items-center gap-1">
                <EyeIcon className="h-4 w-4" />
                <span>{formatViewCount(post.totalView)}</span>
              </div>
              {post.totalLike !== undefined && post.totalLike > 0 && (
                <div className="flex items-center gap-1">
                  <HeartIcon className="h-4 w-4" />
                  <span>{post.totalLike}</span>
                </div>
              )}
              {post.totalShare !== undefined && post.totalShare > 0 && (
                <div className="flex items-center gap-1">
                  <ShareIcon className="h-4 w-4" />
                  <span>{post.totalShare}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              {/* Like button */}
              <button
                onClick={handleLike}
                className={`p-2 rounded-full transition-colors ${
                  isLiked 
                    ? 'text-red-500 hover:text-red-600' 
                    : 'text-m2-outline hover:text-red-500'
                }`}
                title="Thích bài viết"
              >
                {isLiked ? (
                  <HeartSolidIcon className="h-4 w-4" />
                ) : (
                  <HeartIcon className="h-4 w-4" />
                )}
              </button>

              {/* Share dropdown - simplified for now */}
              <button
                onClick={(e) => handleShare(e, 'facebook')}
                className="p-2 rounded-full text-m2-outline hover:text-m2-primary transition-colors"
                title="Chia sẻ bài viết"
              >
                <ShareIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ArticleCard;